import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import Loading from "../ui/Loading";
import { RootState } from "../../store/indexRedux";
import { useTranslation } from "react-i18next";

import CardInstallation from "./CardInstallation";

import SypplyIcon from "../svg/SupplyIcon";
import RenewableIcon from "../svg/RenewableIcon";
import EmbededBessIcon from "../svg/EmbededBessIcon";
import CorporateIcon from "../svg/CorporateIcon";
import { getFlexContractStatus } from "../../functions/flexServices";

interface ServiceType {
  key: string;
  title: string;
  desc: string;
  demoCount: number | null;
  link: string;
  icon: React.ReactElement;
  smartDesc?: string;
  message?: string;
  serviceType?: "injection" | "offtake" | "bess";
  comingSoon?: boolean;
}

const ENDED_STATUSES = new Set(["ended", "expired"]);

const ProductServiceSummary: React.FC = () => {
  const { t } = useTranslation("InstallationLanding");
  const { loading: installationLoading } = useSelector((state: RootState) => state.installation);
  const { loading: servicesLoading } = useSelector((state: RootState) => state.companyServiceSlice);
  const { data: portfolioData, loading: portfolioLoading } = useSelector(
    (state: RootState) => state.companyPortfolio
  );


  const { injectionCount, offtakeCount, embeddedBESSCount, pvCurtailmentCount } = useMemo(() => {
    if (!portfolioData?.services) {
      return { injectionCount: 0, offtakeCount: 0, embeddedBESSCount: 0, pvCurtailmentCount: 0 };
    }

    const { injection, offtake, bess, pvCurtailment } = portfolioData.services;

    const injectionCount = injection.filter((s) => !ENDED_STATUSES.has(s.status)).length;
    const offtakeCount = offtake.filter((s) => !ENDED_STATUSES.has(s.status)).length;

    const embeddedBESSCount = bess.filter((s) => {
      const status = s.contract_status ?? getFlexContractStatus(s);
      return status !== "ended";
    }).length;

    const pvCurtailmentCount = pvCurtailment.filter((s) => {
      const status = s.contract_status ?? getFlexContractStatus(s);
      return status !== "ended";
    }).length;

    return { injectionCount, offtakeCount, embeddedBESSCount, pvCurtailmentCount };
  }, [portfolioData]);

  const embeddedBESSDisabled = injectionCount === 0 || offtakeCount === 0;
  const pvCurtailmentDisabled = injectionCount === 0;

  const serviceTypes: ServiceType[] = [
    {
      key: "injection",
      title: injectionCount === 0 ? t("injection.offer") : t("injection.title"),
      desc: t("injection.description"),
      demoCount: injectionCount,
      link: "/services",
      // smartDesc: t('smartSteering'),
      icon: <SypplyIcon />,
      serviceType: "injection",
    },
    {
      key: "offtake",
      title: offtakeCount === 0 ? t("offtake.offer") : t("offtake.title"),
      desc: t("offtake.description"),
      demoCount: offtakeCount,
      link: "/services",
      icon: <RenewableIcon />,
      serviceType: "offtake",
      // smartDesc: t('smartSteering'),
    },
    {
      key: "embeddedBESS",
      title: t("embeddedBESS.title"),
      desc: t("embeddedBESS.description"),
      demoCount: embeddedBESSDisabled ? null : embeddedBESSCount,
      smartDesc: t("smartSteering"),
      link: "/bess",
      icon: <EmbededBessIcon />,
      message: embeddedBESSDisabled ? t("requiresActivation") : undefined,
      serviceType: "bess",
      comingSoon: false,
    },
    {
      key: "pv-curtailment",
      title: "PV Curtailment",
      desc: t("energySharing.description"),
      demoCount: pvCurtailmentDisabled ? null : pvCurtailmentCount,
      link: "/pv-curltaiment",
      icon: <CorporateIcon />,
      message: pvCurtailmentDisabled ? t("requiresInjection") : undefined,
      comingSoon: false,
    },
    // {
    //   key: 'corporatePPA',
    //   title: t('corporatePPA.title'),
    //   desc: t('corporatePPA.description'),
    //   demoCount: null,
    //   link: '/services/corporate-ppa',
    //   icon: <EnergySharingIcon />,
    //   message: 'Not avalivable right now',
    // },

    // {
    //   key: 'utilityScaleBESS',
    //   title: t('utilityScaleBESS.title'),
    //   desc: t('utilityScaleBESS.description'),
    //   demoCount: null,
    //   link: '/services/utility-scale-bess',
    //   icon: <UnityScaleIcon />,
    //   message: 'Not avalivable right now',
    // },
  ];

  const isProcessing = installationLoading || servicesLoading || portfolioLoading;

  const showBanner = injectionCount === 0 && offtakeCount === 0;

  if (isProcessing) {
    return <Loading />;
  }

  return (
    <div className=" md:mx-16  pb-[80px] ">
      <div className=" mt-20 mb-16">
        <h1 className="text-primary-600 dark:text-white lg:text-[1.875rem] font-semibold ">{t("titleInstallation")}</h1>
        {showBanner && (
          <div className="p-5 mt-12 border border-[#FED7AA] rounded-md bg-[#FFEDD5] text-[#C2410C] text-sm">
            <p className="font-semibold">{t("unverifiedCompany")}</p>
            <p>{t("discoverYuso")}</p>
          </div>
        )}
      </div>
      <div className="grid lg:grid-rows-1 lg:grid-cols-3 md:grid-rows-2 md:grid-cols-2 gap-8 grid-rows-1 grid-cols-1 rounded-lg ">
        {serviceTypes.map((service) => (
          <CardInstallation
            key={service.key}
            title={service.title}
            demoCount={service.demoCount}
            link={service.link}
            icon={service.icon}
            smartDesc={service.smartDesc}
            desc={service.desc}
            message={service.message}
            serviceType={service.serviceType}
            comingSoon={service.comingSoon}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductServiceSummary;
